import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { SwapButton } from '@root/entities/trade-type/ui'
import { InputPercentage } from '@root/shared/ui-kit'

import {
  instantSellQuickOptionsAtom,
  reatomInstantSell,
} from '../../atoms/reatom-instant-sell'
import { InstantTradeQuickOptionsEditButton } from '../instant-trade-buttons-edit-button'
import { InstantTradeSellButtonsForm } from '../instant-trade-sell-buttons-form'

import { InstantTradeSwapInput } from './common/instant-trade-swap-input'
import { InstantTradeSellQuote } from './instant-trade-sell-quote'
import { InstantTradeSwapOption } from './instant-trade-swap-option'

import type { PairModel } from '@root/entities/pair'

type Props = {
  pair: PairModel
}

export const InstantTradeSwapSell: React.FC<Props> = ({ pair }) => {
  const { t } = useTranslation()

  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomInstantSell({ pair }),
    [pair],
  )
  const { inputModel } = model
  const [amountRaw] = useAtom(inputModel.amountRawAtom)
  const [percentageRaw, setPercentageRaw] = useState('0')
  const [showEditQuickOptionsForm, setShowEditQuickOptionsForm] =
    useState(false)
  const [quoteState] = useAtom(model.quoteModel.quoteStateAtom)
  const initialize = useAction(model.initialize)

  const [requestState] = useAtom(model.executeSwapModel)
  const [validationError] = useAtom(model.validationAtom)
  const [balance] = useAtom(model.inputModel.balanceAtom)

  const setAmountRaw = useAction(inputModel.setAmountRawAction)
  const setQuickAmount = useAction(inputModel.setQuickAmount)
  const executeQuickSell = useAction(model.quickSellAction)
  const executeSell = useAction(model.processSellAction)

  const [instantSellQuickOptions] = useAtom(instantSellQuickOptionsAtom)

  useEffect(() => {
    const dispose = initialize()
    return () => {
      dispose()
    }
  }, [initialize, model])

  const renderQuickOptions = () => (
    <QuickOptions>
      {showEditQuickOptionsForm ? (
        <InstantTradeSellButtonsForm
          onDone={() => setShowEditQuickOptionsForm(false)}
        />
      ) : (
        <QuickSwapOptions>
          <InstantTradeQuickOptionsEditButton
            type="button"
            state="edit"
            onClick={() => setShowEditQuickOptionsForm(true)}
          />
          {instantSellQuickOptions.map((it, index) => (
            <InstantTradeSwapOption
              swapDirection={'sell'}
              key={index}
              disabled={balance.eq(0)}
              title={`${it.toNumber()} %`}
              onClick={() => executeQuickSell(it.div(100).toNumber())}
            />
          ))}
        </QuickSwapOptions>
      )}
      <InputPercentage
        size="s"
        value={percentageRaw}
        onChange={({ percents, value }) => {
          setPercentageRaw(value)
          if (typeof percents === 'number') {
            setQuickAmount(percents)
          }
        }}
        css={css`
          --input-height: 30px;
          --input-border: 1px solid var(--background-4);
          --input-border-active: 1px solid var(--background-6);
        `}
      />
    </QuickOptions>
  )

  return (
    <Root>
      {renderQuickOptions()}
      <AmountSection>
        <Group>
          <InstantTradeSwapInput
            currency={pair.quote}
            value={amountRaw}
            onChange={(value) => setAmountRaw(value)}
          />
          <SwapButton
            swapDirection="sell"
            onClick={() => executeSell()}
            disabled={!!validationError || !AsyncStateN.isSuccess(quoteState)}
            busy={AsyncStateN.isLoading(requestState)}
          >
            {t('trading.sell')}
          </SwapButton>
        </Group>
        <InstantTradeSellQuote pair={pair} model={model} />
      </AmountSection>
    </Root>
  )
}

const Root = styled.div`
  gap: 8px;

  display: grid;
  overflow: hidden;
  align-items: center;
  grid-template-columns: minmax(0, 1fr);
`

const AmountSection = styled.div`
  display: grid;
  gap: 4px;
`
const QuickSwapOptions = styled.div`
  display: grid;
  grid-template-columns: 30px 1fr 1fr 1fr;
  gap: 2px;
`

const QuickOptions = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  grid-auto-flow: column;
  gap: 2px;
`

const Group = styled.div`
  display: grid;
  grid-template-columns: 1fr 67px;
  gap: 4px;
`
